import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import { BsLayoutSidebar } from 'react-icons/bs';
import { IoAdd, IoAlbumsOutline, IoChevronDown, IoChevronForward, IoEllipsisVerticalSharp } from 'react-icons/io5';

import {
    Affix,
    Box,
    BoxProps,
    Drawer,
    Flex,
    Group,
    Breadcrumbs as MantineBreadcrumbs,
    Menu,
    Stack,
    Text,
    Tooltip,
    Transition,
    UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';

import { SidebarService } from '@diagram/services/SidebarService';
import { SidebarType } from '@diagram/state/sidebar';

import { ProjectHelpers } from 'helpers/ProjectHelpers';
import { RouterHelpers } from 'helpers/RouterHelpers';
import { TeamHelpers } from 'helpers/TeamHelpers';
import { TextHelpers } from 'helpers/TextHelpers';

import { EIcon } from 'components/logo/EIcon';
import { useSupportCompanies } from 'components/project-overview/hooks/use-support-companies';
import { SIDEBAR_NAV_WIDTH } from 'components/sidebar-nav/SidebarNav';
import { useScroll } from 'hooks/dom/use-scroll';
import { useCurrentProject } from 'hooks/use-current-project';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentTeams } from 'hooks/use-current-teams';
import { useCurrentUser } from 'hooks/use-current-user';
import { useLocalUserInfo } from 'hooks/use-local-user-info';
import { useProjectCompanies } from 'hooks/use-project-companies';
import { useProjectTeam } from 'hooks/use-project-team';
import { useRecentProjects } from 'hooks/use-recent-projects';
import { useSidebarNav } from 'hooks/use-sidebar-nav';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { sidebarNavState } from 'state/sidebar-nav';

import cx from './Breadcrumbs.module.scss';

const MAX_BREADCRUMB_LENGTH = 40;
const MAX_BREADCRUMB_LENGTH_MOBILE = 20;

export type BreadcrumbProps = {
    title?: string;
    isSticky?: boolean;
    rightSection?: React.ReactNode;
    showHomeAsLogo?: boolean;
};

const Breadcrumbs: FC<BreadcrumbProps & BoxProps> & {
    Sticky: typeof BreadcrumbsSticky;
    Content: typeof BreadcrumbsContent;
    Floating: typeof BreadcrumbsFloating;
    RightSection: typeof BreadcrumbsRightSection;
    Home: typeof BreadcrumbsHome;
} = ({ title, isSticky = true, rightSection, ...props }) => {
    return (
        <>
            <Box className={`${cx.root} ${cx.fullWidth}`} {...props}>
                <Breadcrumbs.Content rightSection={rightSection} title={title} />
            </Box>
            {isSticky && <Breadcrumbs.Sticky rightSection={rightSection} title={title} {...props} />}
        </>
    );
};

const BreadcrumbsSticky = ({ title, rightSection, ...props }: BreadcrumbProps & BoxProps) => {
    const { top: scroll } = useScroll();
    const { isOpen } = useSidebarNav();

    const [hero, setHero] = useState<Element | null>(null);

    useEffect(() => {
        const findHero = document.querySelector('[data-hero]');

        if (findHero) {
            setHero(findHero);
        }
    }, []);

    const scrollOffset = hero ? hero.getBoundingClientRect().height : 200;

    return (
        <Affix position={{ top: 0, left: isOpen ? SIDEBAR_NAV_WIDTH : 0, right: 0 }} visibleFrom="md">
            <Transition transition="slide-down" mounted={scroll > scrollOffset} exitDelay={50}>
                {(transitionStyles) => (
                    <Box className={`${cx.root} ${cx.fullWidth} ${cx.sticky}`} style={transitionStyles} {...props}>
                        <Breadcrumbs.Content rightSection={rightSection} title={title} />
                    </Box>
                )}
            </Transition>
        </Affix>
    );
};

const BreadcrumbsFloating = ({
    title,
    fullWidth,
    rightSection,
    isSticky = true,
    showHomeAsLogo,
    position,
    ...props
}: {
    fullWidth?: boolean;
    position?: 'relative';
} & BreadcrumbProps &
    BoxProps) => {
    const { isOpen } = useSidebarNav();

    if (fullWidth) {
        return (
            <>
                <Box
                    className={`${cx.root} ${cx.floatingFullWidth}`}
                    data-breadcrumbs
                    data-sidebar-open={isOpen}
                    {...props}
                >
                    <Breadcrumbs.Content rightSection={rightSection} title={title} showHomeAsLogo={showHomeAsLogo} />
                </Box>
                {isSticky && <Breadcrumbs.Sticky rightSection={rightSection} title={title} {...props} />}
            </>
        );
    }

    return (
        <Box
            className={cx.floatingWrapper}
            data-breadcrumbs
            data-sidebar-open={isOpen}
            data-position={position}
            {...props}
        >
            <Box className={`${cx.root} ${cx.floating}`}>
                <Breadcrumbs.Content title={title} showHomeAsLogo={showHomeAsLogo} />
            </Box>

            {rightSection && (
                <Box className={cx.floatingRightSection}>
                    <Breadcrumbs.RightSection rightSection={rightSection} />
                </Box>
            )}
        </Box>
    );
};

const BreadcrumbsContent = ({ title, rightSection, showHomeAsLogo }: BreadcrumbProps) => {
    const router = useRouter();
    const isProjectEditor = router.pathname.includes('/projects/[projectId]/editor');

    const project = useCurrentProject();
    const { team } = useProjectTeam();
    const currentTeams = useCurrentTeams();

    const isTeamMember = !!team && currentTeams.some((currentTeam) => currentTeam.id === team.id);

    const supportCompanies = useSupportCompanies();
    const { memberCompanyIds: projectCompanyIds } = useProjectCompanies(project?.id);
    const isSupportProject =
        !isTeamMember && !!project && supportCompanies.some((company) => projectCompanyIds.includes(company.id));

    const { recentProjects } = useRecentProjects();

    const { width } = useViewportSize();
    const showManyBreadcrumbs = width >= 640;

    const breadcrumbs: {
        label: string;
        url: string;
        rightSection?: React.ReactNode;
        menuItems?: React.ReactNode;
    }[] = [];

    if (router.pathname.includes('/projects/') && showManyBreadcrumbs) {
        if (isSupportProject) {
            breadcrumbs.push({
                label: 'Support projects',
                url: ProjectHelpers.urls.supportProjects(),
            });
        } else if (!isTeamMember && project?.isReferenceDesign) {
            breadcrumbs.push({
                label: 'Reference designs',
                url: RouterHelpers.urls.designs.search(),
            });
        } else {
            if (isTeamMember && team) {
                breadcrumbs.push({
                    label: team.name,
                    url: TeamHelpers.urls.settings(team.id),
                });
            }

            breadcrumbs.push({
                label: 'Projects',
                url: ProjectHelpers.urls.overview(),
                menuItems: (
                    <>
                        {recentProjects.slice(0, 4).map((project) => (
                            <Menu.Item
                                key={project.id}
                                px="xs"
                                fz="xs"
                                c="dimmed"
                                component="a" // don't user Next.js Link for diagram state issues
                                href={ProjectHelpers.urls.editor(project.combinedSlug)}
                            >
                                {TextHelpers.getTextWithEllipsis(project.name, MAX_BREADCRUMB_LENGTH)}
                            </Menu.Item>
                        ))}
                        {recentProjects.length > 0 && <Menu.Divider />}
                        <Menu.Item
                            px="xs"
                            fz="xs"
                            fw={500}
                            component={Link}
                            leftSection={<IoAlbumsOutline />}
                            href={ProjectHelpers.urls.overview()}
                        >
                            All Projects
                        </Menu.Item>
                        <Menu.Item
                            px="xs"
                            fz="xs"
                            fw={500}
                            component={Link}
                            leftSection={<IoAdd />}
                            href={ProjectHelpers.urls.create()}
                        >
                            Create New Project
                        </Menu.Item>
                    </>
                ),
            });
        }
    } else if (router.pathname.includes('/products/')) {
        breadcrumbs.push({
            label: 'Products',
            url: RouterHelpers.urls.products.search(),
        });
    } else if (router.pathname.includes('/profiles/')) {
        breadcrumbs.push({
            label: 'Profiles',
            url: RouterHelpers.urls.profiles.search(),
        });
    }

    const toggleSidebarMobile = () => {
        sidebarNavState.isOpen = !sidebarNavState.isOpen;
    };

    const showTitleTooltip = title && title.length > MAX_BREADCRUMB_LENGTH;

    return (
        <>
            <UnstyledButton onClick={toggleSidebarMobile} className={cx.sidebarToggle} hiddenFrom="md">
                <BsLayoutSidebar />
            </UnstyledButton>

            <MantineBreadcrumbs separator={<IoChevronForward />} className={cx.breadcrumbs}>
                <Breadcrumbs.Home showHomeAsLogo={showHomeAsLogo} />

                {breadcrumbs.map((breadcrumb) => (
                    <Menu
                        trigger="click-hover"
                        disabled={!breadcrumb.menuItems}
                        position="bottom-start"
                        offset={{ mainAxis: 14, crossAxis: -16 }}
                        shadow="xl"
                    >
                        <Group key={breadcrumb.label} gap={6}>
                            <Menu.Target>
                                <Text inherit component={Link} href={breadcrumb.url} className={cx.link}>
                                    {breadcrumb.label}

                                    {breadcrumb.menuItems && (
                                        <IoChevronDown
                                            size={10}
                                            style={{
                                                color: 'var(--mantine-color-gray-5)',
                                                verticalAlign: 'middle',
                                                marginLeft: 3,
                                            }}
                                        />
                                    )}
                                </Text>
                            </Menu.Target>

                            {breadcrumb.rightSection}
                        </Group>

                        <Menu.Dropdown bd="none" style={{ '--menu-item-hover': 'var(--mantine-color-gray-0)' }}>
                            {breadcrumb.menuItems}
                        </Menu.Dropdown>
                    </Menu>
                ))}

                {title && isProjectEditor && (
                    <Tooltip label="Click to edit" position="bottom" offset={12} openDelay={1000}>
                        <Text
                            inherit
                            className={cx.link}
                            onClick={() => {
                                SidebarService.open(SidebarType.PROJECT);
                            }}
                        >
                            {TextHelpers.getTextWithEllipsis(title, MAX_BREADCRUMB_LENGTH)}
                        </Text>
                    </Tooltip>
                )}
                {title && !isProjectEditor && (
                    <>
                        <Box visibleFrom="md">
                            <Tooltip label={title} disabled={!showTitleTooltip} position="bottom-start" offset={12}>
                                <span>{TextHelpers.getTextWithEllipsis(title, MAX_BREADCRUMB_LENGTH)}</span>
                            </Tooltip>
                        </Box>
                        <Box hiddenFrom="md">
                            {TextHelpers.getTextWithEllipsis(title, MAX_BREADCRUMB_LENGTH_MOBILE)}
                        </Box>
                    </>
                )}
            </MantineBreadcrumbs>

            {rightSection && (
                <Box className={cx.rightSection}>
                    <Breadcrumbs.RightSection rightSection={rightSection} />
                </Box>
            )}
        </>
    );
};

const BreadcrumbsRightSection = ({ rightSection }: BreadcrumbProps) => {
    const [rightSectionOpen, rightSectionHandlers] = useDisclosure();

    return (
        <>
            <Group gap={4} visibleFrom="md">
                {rightSection}
            </Group>
            <UnstyledButton
                ml="auto"
                hiddenFrom="md"
                onClick={rightSectionHandlers.toggle}
                className={cx.sidebarToggle}
            >
                <IoEllipsisVerticalSharp />
            </UnstyledButton>
            <Drawer
                className={cx.rightSectionDrawer}
                withCloseButton={false}
                size={SIDEBAR_NAV_WIDTH}
                opened={rightSectionOpen}
                onClose={rightSectionHandlers.close}
                position="right"
            >
                <Stack gap="xs" align="start">
                    {rightSection}
                </Stack>
            </Drawer>
        </>
    );
};

const BreadcrumbsHome = ({ showHomeAsLogo }: { showHomeAsLogo?: boolean }) => {
    const team = useCurrentTeam();
    const user = useCurrentUser();
    const workspaceConfig = useWorkspaceConfig();

    const { referrer } = useLocalUserInfo();

    const url = RouterHelpers.urls.homepage(team, user, referrer);

    if (showHomeAsLogo) {
        return (
            <Flex c="inherit" component={Link} href={url} data-breadcrumbs-logo>
                {workspaceConfig.logoUrl ? (
                    <img src={workspaceConfig.logoUrl} alt="Logo" height={16} />
                ) : (
                    <EIcon width={24} />
                )}
            </Flex>
        );
    }

    return (
        <Text inherit component={Link} href={url} className={cx.link}>
            Home
        </Text>
    );
};

Breadcrumbs.Sticky = BreadcrumbsSticky;
Breadcrumbs.Content = BreadcrumbsContent;
Breadcrumbs.Floating = BreadcrumbsFloating;
Breadcrumbs.RightSection = BreadcrumbsRightSection;
Breadcrumbs.Home = BreadcrumbsHome;

export { Breadcrumbs };
